import { useState, useEffect, useRef, useCallback } from 'react';
import { useInvoices, useDashboard } from '../../hooks/useData';
import { FileUpload, QuickUploadButton, compressImage } from '../../components/shared/FileUpload';
import { DataTable, AlertCard } from '../../components/shared/KPIComponents';
import { api, getCurrentMonth, formatCurrency } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Trash2, FileText, Package, Brain, Loader2, TrendingDown, TrendingUp, AlertTriangle, Star, Bell, ArrowUp, ArrowDown, Fuel, Receipt, Eye, X, Pencil, Save, Plus, RefreshCw, Camera, ChevronLeft, ChevronRight, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { AvoirsSection, ConsignesSection } from './AvoirsConsignes';
import RetoursMarchandiseSection from '../../components/shared/RetoursMarchandise';
import OptimizeButton from '../../components/shared/OptimizeButton';
import GmailSection from '../../components/shared/GmailSection';

const TYPE_LABELS = {
  essence: 'Essence',
  entretien: 'Entretien',
  fournitures: 'Fournitures',
  telecom: 'Télécom',
  assurance: 'Assurance',
  divers: 'Divers',
};

const TYPE_COLORS = {
  essence: 'bg-amber-500/15 text-amber-400',
  entretien: 'bg-blue-500/15 text-blue-400',
  fournitures: 'bg-purple-500/15 text-purple-400',
  telecom: 'bg-cyan-500/15 text-cyan-400',
  assurance: 'bg-red-500/15 text-red-400',
  divers: 'bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-secondary))]',
};

const PatronFactures = () => {
  const [currentMonth, setCurrentMonth] = useState(getCurrentMonth());
  const { invoices, loading, refetch } = useInvoices(currentMonth);
  const { data: dashboard } = useDashboard(currentMonth);

  const navigateMonth = (dir) => {
    const [yy, mm] = currentMonth.split('-').map(Number);
    let nm = mm + dir, ny = yy;
    if (nm > 12) { nm = 1; ny++; }
    if (nm < 1) { nm = 12; ny--; }
    setCurrentMonth(`${ny}-${String(nm).padStart(2, '0')}`);
  };

  const [yy, mm] = currentMonth.split('-').map(Number);
  const monthLabel = new Date(yy, mm - 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [priceAlerts, setPriceAlerts] = useState([]);
  const [sendingAlerts, setSendingAlerts] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showFournisseurs, setShowFournisseurs] = useState(true);
  
  // Diverse invoices
  const [diverseInvoices, setDiverseInvoices] = useState([]);
  const [selectedDiverse, setSelectedDiverse] = useState(null);
  const [diverseUploading, setDiverseUploading] = useState(false);

  // Manual invoice entry
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const [rescanLoading, setRescanLoading] = useState(null);
  const [manualForm, setManualForm] = useState({
    fournisseur: '',
    date: new Date().toISOString().split('T')[0],
    total_ht: '',
    total_tva: '',
    total_ttc: '',
    cuisine_ht: '',
    boissons_ht: '',
    non_alimentaire_ht: '',
  });

  const resetManualForm = () => {
    setManualForm({
      fournisseur: '',
      date: new Date().toISOString().split('T')[0],
      total_ht: '',
      total_tva: '',
      total_ttc: '',
      cuisine_ht: '',
      boissons_ht: '',
      non_alimentaire_ht: '',
    });
  };

  const [rescanTarget, setRescanTarget] = useState(null);
  const rescanRef = useRef(null);

  const doRescan = useCallback(async (file) => {
    if (!file) return;
    const target = rescanRef._target;
    if (!target) return;
    setRescanLoading(target);
    try {
      const compressed = await compressImage(file);
      const formData = new FormData();
      formData.append('file', compressed);
      await api.put(`/invoices/${target}/rescan`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      toast.success('Facture re-scannee');
      refetch();
      setSelectedInvoice(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur re-scan');
    }
    setRescanLoading(null);
    rescanRef._target = null;
  }, [refetch]);

  // Native event listeners for iOS Safari compatibility
  useEffect(() => {
    const el = rescanRef.current;
    if (!el) return;
    let processing = false;
    const handler = (e) => {
      if (processing) return;
      const file = e.target?.files?.[0];
      if (!file) return;
      processing = true;
      doRescan(file);
      setTimeout(() => { el.value = ''; processing = false; }, 1000);
    };
    el.addEventListener('change', handler);
    el.addEventListener('input', handler);
    return () => {
      el.removeEventListener('change', handler);
      el.removeEventListener('input', handler);
    };
  }, [doRescan]);

  const triggerRescan = (invoiceId) => {
    rescanRef._target = invoiceId;
    setRescanTarget(invoiceId);
    rescanRef.current?.click();
  };



  const handleManualSubmit = async () => {
    if (!manualForm.fournisseur.trim()) {
      toast.error('Veuillez saisir le fournisseur');
      return;
    }
    const ht = parseFloat(manualForm.total_ht) || 0;
    const tva = parseFloat(manualForm.total_tva) || 0;
    const ttc = parseFloat(manualForm.total_ttc) || 0;
    const cuisine = parseFloat(manualForm.cuisine_ht) || 0;
    const boissons = parseFloat(manualForm.boissons_ht) || 0;
    const nonAlim = parseFloat(manualForm.non_alimentaire_ht) || 0;
    const anyAmount = ht || ttc || cuisine || boissons || nonAlim;
    if (!anyAmount) {
      toast.error('Veuillez saisir au moins un montant');
      return;
    }
    // Auto-calc total_ht from categories if not filled
    const calcHT = ht || (cuisine + boissons + nonAlim);
    const calcTTC = ttc || (calcHT + tva);
    setManualSaving(true);
    try {
      const payload = {
        fournisseur: manualForm.fournisseur.trim(),
        date: manualForm.date || new Date().toISOString().split('T')[0],
        total_ht: calcHT,
        total_tva: tva,
        total_ttc: calcTTC,
        cuisine_ht: cuisine,
        boissons_ht: boissons,
        non_alimentaire_ht: nonAlim,
      };
      await api.post('/invoices', payload);
      toast.success(`Facture ${payload.fournisseur} ajoutée`);
      setShowManualForm(false);
      resetManualForm();
      refetch();
    } catch (e) {
      toast.error('Erreur lors de l\'ajout');
    } finally {
      setManualSaving(false);
    }
  };

  const handleUploadSuccess = (data) => {
    refetch();
    setSelectedInvoice(data);
    fetchPriceAlerts();
  };
  
  const fetchPriceAlerts = async () => {
    try {
      const res = await api.get('/price-alerts');
      setPriceAlerts(res.data);
    } catch {}
  };

  const fetchDiverseInvoices = async () => {
    try {
      const res = await api.get(`/diverse-invoices?mois=${currentMonth}`);
      setDiverseInvoices(res.data);
    } catch {}
  };

  useEffect(() => { fetchPriceAlerts(); fetchDiverseInvoices(); }, [currentMonth]);

  const handleDiverseUpload = async (data) => {
    fetchDiverseInvoices();
    setSelectedDiverse(data);
    toast.success(`Facture ${data.type_facture} analysée — TVA: ${formatCurrency(data.total_tva)}`);
  };

  const deleteDiverse = async (id) => {
    try {
      await api.delete(`/diverse-invoices/${id}`);
      toast.success('Facture supprimée');
      fetchDiverseInvoices();
    } catch { toast.error('Erreur'); }
  };

  const sendPriceAlertsTelegram = async () => {
    setSendingAlerts(true);
    try {
      const res = await api.post('/price-alerts/send-telegram');
      toast.success(res.data.message);
    } catch { toast.error('Erreur envoi'); }
    finally { setSendingAlerts(false); }
  };
  
  const handleDelete = async (id) => {
    try {
      await api.delete(`/invoices/${id}`);
      toast.success('Facture supprimée');
      refetch();
    } catch { toast.error('Erreur'); }
  };

  const openEdit = (inv) => {
    setEditInvoice(inv);
    setEditForm({
      fournisseur: inv.fournisseur || '',
      date: inv.date || '',
      total_ht: inv.total_ht || 0,
      total_tva: inv.total_tva || 0,
      total_ttc: inv.total_ttc || 0,
      cuisine_ht: inv.cuisine_ht || 0,
      boissons_ht: inv.boissons_ht || 0,
      non_alimentaire_ht: inv.non_alimentaire_ht || 0,
    });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/invoices/${editInvoice.id}`, editForm);
      toast.success('Facture corrigée');
      setEditInvoice(null);
      refetch();
    } catch { toast.error('Erreur de sauvegarde'); }
  };
  
  const totalCuisine = invoices.reduce((sum, i) => sum + (i.cuisine_ht || 0), 0);
  const totalBoissons = invoices.reduce((sum, i) => sum + (i.boissons_ht || 0), 0);
  const totalNonAlim = invoices.reduce((sum, i) => sum + (i.non_alimentaire_ht || 0), 0);
  const totalTTC = invoices.reduce((sum, i) => sum + (i.total_ttc || 0), 0);
  const totalHT = invoices.reduce((sum, i) => sum + (i.total_ht || 0), 0);

  // Top fournisseur dynamique (groupé par nom normalisé)
  const fournisseursMap = invoices.reduce((acc, inv) => {
    const raw = (inv.fournisseur || '').trim();
    if (!raw) return acc;
    // Normalize: lowercase, remove accents, keep first significant word
    const norm = raw.toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/s\.?a\.?s?\.?|s\.?a\.?r\.?l\.?|sarl|sas/g, '')
      .split(/[\s,.-]+/)[0] || raw.toLowerCase();
    if (!acc[norm]) acc[norm] = { name: raw, total: 0, count: 0 };
    acc[norm].total += inv.total_ttc || 0;
    acc[norm].count += 1;
    return acc;
  }, {});
  const allFournisseurs = Object.values(fournisseursMap)
    .sort((a, b) => b.total - a.total);
  const topFournisseurs = allFournisseurs.slice(0, 3);
  const topFournisseur = topFournisseurs[0] || { name: '-', total: 0 };
  const maxFournisseurTotal = topFournisseur.total || 1;

  const caHT = dashboard?.objectif_ca ? dashboard.objectif_ca / 1.1 : 0;
  const ratioCuisine = caHT > 0 ? (totalCuisine / caHT) * 100 : 0;

  // Diverse totals
  const diverseTotalTTC = diverseInvoices.reduce((sum, i) => sum + (i.total_ttc || 0), 0);
  const diverseTotalTVA = diverseInvoices.reduce((sum, i) => sum + (i.total_tva || 0), 0);

  const runAiAnalysis = async () => {
    if (invoices.length === 0) { toast.error('Aucune facture à analyser'); return; }
    setAiLoading(true);
    try {
      const res = await api.post('/ai/analyze-factures', { month: currentMonth, ca_ht: caHT });
      setAiAnalysis(res.data);
      toast.success('Analyse terminée');
    } catch (e) {
      toast.error('Erreur analyse IA');
    } finally { setAiLoading(false); }
  };

  const incompleteCount = invoices.filter(i => !i.produits || i.produits.length === 0).length;

  const columns = [
    { header: 'Date', render: (row) => <span className="font-mono text-sm">{row.date}</span> },
    { header: 'Fournisseur', render: (row) => (
      <div className="flex items-center gap-2">
        <span className="font-medium">{row.fournisseur}</span>
        {row.produits && row.produits.length > 0 ? (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 font-mono">{row.produits.length} art.</span>
        ) : (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400" title="Aucun produit extrait — re-scannez cette facture">0 art.</span>
        )}
      </div>
    )},
    { header: 'HT', render: (row) => <span className="font-mono text-xs text-[rgb(var(--lm-text-muted))]">{formatCurrency(row.total_ht)}</span> },
    { header: 'TTC', render: (row) => <span className="font-mono text-lemas-gold">{formatCurrency(row.total_ttc)}</span> },
    { header: 'TVA', render: (row) => (
      <button
        onClick={async () => {
          const avecTva = row.total_tva === 0 || row.sans_tva;
          try {
            const res = await api.put(`/invoices/${row.id}/toggle-tva`, { avec_tva: avecTva, taux: 20 });
            if (res.data.message) toast.success(res.data.message);
            refetch();
          } catch { toast.error('Erreur'); }
        }}
        className={`text-[10px] font-medium px-2 py-0.5 rounded-full cursor-pointer transition-colors ${
          row.total_tva > 0 && !row.sans_tva
            ? 'bg-green-400/15 text-green-400 hover:bg-red-400/15 hover:text-red-400'
            : 'bg-red-400/15 text-red-400 hover:bg-green-400/15 hover:text-green-400'
        }`}
        title={row.total_tva > 0 ? `TVA: ${formatCurrency(row.total_tva)} — Cliquer pour retirer` : 'Sans TVA — Cliquer pour ajouter'}
        data-testid={`toggle-tva-${row.id}`}
      >
        {row.total_tva > 0 && !row.sans_tva ? `${formatCurrency(row.total_tva)}` : 'Sans TVA'}
      </button>
    )},
    { header: 'Cuisine HT', render: (row) => <span className="font-mono">{formatCurrency(row.cuisine_ht)}</span> },
    { header: 'Boissons HT', render: (row) => <span className="font-mono">{formatCurrency(row.boissons_ht)}</span> },
    { header: '', render: (row) => (
      <div className="flex gap-1">
        <button onClick={() => triggerRescan(row.id)} className="inline-flex items-center justify-center p-1.5 text-[rgb(var(--lm-text-secondary))] hover:text-lemas-gold cursor-pointer" data-testid={`rescan-invoice-${row.id}`} title="Re-scanner">
          {rescanLoading === row.id ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
        </button>
        <Button variant="ghost" size="sm" onClick={() => openEdit(row)} className="text-[rgb(var(--lm-text-secondary))] hover:text-lemas-gold" data-testid={`edit-invoice-${row.id}`}><Pencil size={14} /></Button>
        <Button variant="ghost" size="sm" onClick={() => setSelectedInvoice(row)} className="text-[rgb(var(--lm-text-secondary))] hover:text-[rgb(var(--lm-text))]"><FileText size={14} /></Button>
        <Button variant="ghost" size="sm" onClick={() => handleDelete(row.id)} className="text-lemas-danger hover:text-red-400"><Trash2 size={14} /></Button>
      </div>
    )},
  ];
  
  return (
    <div className="space-y-6" data-testid="patron-factures">
      {/* Rescan input — iOS compatible with native listeners */}
      <input ref={rescanRef} type="file" accept="image/*" className="sr-only" style={{ position: 'fixed', top: '-1000px' }} />
      <div className="flex items-center justify-between animate-slide-up">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-1">Achats</p>
          <div className="flex items-center gap-3">
            <button onClick={() => navigateMonth(-1)} className="p-1.5 rounded-lg hover:bg-[rgb(var(--lm-surface))]" data-testid="factures-month-prev">
              <ChevronLeft size={18} className="text-lemas-gold" />
            </button>
            <h2 className="font-title text-2xl text-[rgb(var(--lm-text))] capitalize">{monthLabel}</h2>
            <button onClick={() => navigateMonth(1)} className="p-1.5 rounded-lg hover:bg-[rgb(var(--lm-surface))]" data-testid="factures-month-next">
              <ChevronRight size={18} className="text-lemas-gold" />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setShowManualForm(true)} size="sm" variant="outline" className="border-lemas-gold/40 text-lemas-gold hover:bg-lemas-gold/10" data-testid="manual-invoice-btn">
            <Plus size={14} className="mr-1" />
            Saisie manuelle
          </Button>
          <Button onClick={runAiAnalysis} disabled={aiLoading || invoices.length === 0} size="sm" className="bg-lemas-gold text-lemas-bg" data-testid="ai-factures-btn">
            {aiLoading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Brain size={14} className="mr-1" />}
            Analyse IA
          </Button>
          <OptimizeButton page="achats" />
        </div>
      </div>
      
      <FileUpload onUpload={handleUploadSuccess} endpoint="/analyze-invoice" source="patron" variant="gold" />
      
      {/* Gmail integration */}
      <GmailSection onImported={refetch} />
      
      {ratioCuisine > 30 && (
        <AlertCard type="danger" title="Ratio achats cuisine élevé" message={`${ratioCuisine.toFixed(1)}% > 30% du CA HT`} />
      )}
      
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '60ms', animationFillMode: 'backwards' }}>
          <Package className="mb-1 opacity-50" size={16} style={{ color: 'rgb(var(--lm-gold))' }} />
          <span className="kpi-label">Cuisine HT</span>
          <p className="font-mono text-xl text-[rgb(var(--lm-text))]">{formatCurrency(totalCuisine)}</p>
        </div>
        <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '120ms', animationFillMode: 'backwards' }}>
          <span className="kpi-label">Boissons HT</span>
          <p className="font-mono text-xl text-[rgb(var(--lm-text))]">{formatCurrency(totalBoissons)}</p>
        </div>
        <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '180ms', animationFillMode: 'backwards' }}>
          <span className="kpi-label">Non-alim HT</span>
          <p className="font-mono text-xl text-[rgb(var(--lm-text))]">{formatCurrency(totalNonAlim)}</p>
        </div>
        <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '240ms', animationFillMode: 'backwards', borderColor: 'rgb(var(--lm-gold) / 0.2)' }} data-testid="top-fournisseur-kpi">
          <span className="kpi-label">Top fournisseur</span>
          <p className="font-mono text-xl truncate" style={{ color: 'rgb(var(--lm-gold))' }}>
            {topFournisseur.name !== '-' ? topFournisseur.name.split(' ')[0].toUpperCase() : '-'}
          </p>
          <p className="font-mono text-xs text-[rgb(var(--lm-text-muted))] mt-1">{formatCurrency(topFournisseur.total)}</p>
          {topFournisseurs.length > 1 && (
            <div className="mt-2 pt-2 border-t border-[rgb(var(--lm-border))] space-y-0.5">
              {topFournisseurs.slice(1).map((f, i) => (
                <div key={i} className="flex items-center justify-between text-[10px] text-[rgb(var(--lm-text-muted))]">
                  <span className="truncate">{f.name.split(' ')[0]}</span>
                  <span className="font-mono">{formatCurrency(f.total)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Depenses par fournisseur ── */}
      {allFournisseurs.length > 0 && (
        <div className="lm-card p-5 animate-slide-up" style={{ animationDelay: '280ms', animationFillMode: 'backwards' }} data-testid="fournisseurs-breakdown">
          <button
            onClick={() => setShowFournisseurs(!showFournisseurs)}
            className="w-full flex items-center justify-between mb-3 hover:opacity-80 transition-opacity"
            data-testid="toggle-fournisseurs-btn"
          >
            <div className="flex items-center gap-2">
              <Users size={16} className="text-[rgb(var(--lm-gold))]" />
              <h3 className="font-title text-base">Depenses par fournisseur</h3>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgb(var(--lm-gold)/0.15)] text-[rgb(var(--lm-gold))] font-mono">
                {allFournisseurs.length}
              </span>
            </div>
            {showFournisseurs ? <ChevronUp size={16} className="text-[rgb(var(--lm-text-muted))]" /> : <ChevronDown size={16} className="text-[rgb(var(--lm-text-muted))]" />}
          </button>
          {showFournisseurs && (
            <div className="space-y-2 animate-fade-in">
              {allFournisseurs.map((f, i) => {
                const pct = (f.total / maxFournisseurTotal) * 100;
                const sharePct = totalTTC > 0 ? (f.total / totalTTC) * 100 : 0;
                return (
                  <div key={i} className="flex items-center gap-3 py-1">
                    <span className="text-[11px] font-mono text-[rgb(var(--lm-text-muted))] w-6 shrink-0">#{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium truncate">{f.name}</span>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-[10px] text-[rgb(var(--lm-text-muted))] font-mono">{f.count} fact.</span>
                          <span className="text-[10px] text-[rgb(var(--lm-text-muted))] font-mono w-12 text-right">{sharePct.toFixed(0)}%</span>
                          <span className="font-mono text-sm text-[rgb(var(--lm-text))] w-24 text-right">{formatCurrency(f.total)}</span>
                        </div>
                      </div>
                      <div className="h-1.5 rounded-full bg-[rgb(var(--lm-border))] overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${pct}%`,
                            background: i === 0 ? 'rgb(var(--lm-gold))' : i < 3 ? 'rgb(var(--lm-gold) / 0.6)' : 'rgb(var(--lm-text-muted) / 0.5)'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="pt-2 mt-2 border-t border-[rgb(var(--lm-border))] flex items-center justify-between text-xs">
                <span className="text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Total achats TTC</span>
                <span className="font-mono font-medium">{formatCurrency(totalTTC)}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Incomplete invoices alert */}
      {incompleteCount > 0 && (
        <div className="lm-card p-4 border-amber-500/30 animate-slide-up" data-testid="incomplete-invoices-alert">
          <div className="flex items-center gap-3">
            <Camera className="text-amber-400 flex-shrink-0" size={18} />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-400">{incompleteCount} facture{incompleteCount > 1 ? 's' : ''} sans produits</p>
              <p className="text-xs text-[rgb(var(--lm-text-muted))]">Re-scannez ces factures avec le bouton camera pour que l'IA extraie les articles</p>
            </div>
          </div>
        </div>
      )}

      {/* Price Alerts */}
      {priceAlerts.length > 0 && (
        <div className="kpi-card border-orange-400/30" data-testid="price-alerts">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-orange-400" size={18} />
              <span className="font-medium text-orange-400 text-sm">{priceAlerts.length} variation(s) de prix</span>
            </div>
            <Button onClick={sendPriceAlertsTelegram} disabled={sendingAlerts} size="sm" variant="outline" className="border-orange-400/30 text-orange-400 text-xs" data-testid="send-price-telegram">
              {sendingAlerts ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Bell size={12} className="mr-1" />}
              Telegram
            </Button>
          </div>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {priceAlerts.map((a, i) => (
              <div key={i} className={`flex items-center justify-between p-2 rounded text-sm ${a.tendance === 'hausse' ? 'bg-red-900/10' : 'bg-green-900/10'}`}>
                <div className="flex items-center gap-2 min-w-0">
                  {a.tendance === 'hausse' ? <ArrowUp size={14} className="text-lemas-danger shrink-0" /> : <ArrowDown size={14} className="text-lemas-green shrink-0" />}
                  <span className="truncate">{a.produit}</span>
                  <span className="text-xs text-[rgb(var(--lm-text-muted))]">{a.fournisseur}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="font-mono text-xs text-[rgb(var(--lm-text-secondary))]">{a.prix_ancien}€→{a.prix_actuel}€</span>
                  <span className={`font-mono text-xs font-medium ${a.tendance === 'hausse' ? 'text-lemas-danger' : 'text-lemas-green'}`}>
                    {a.tendance === 'hausse' ? '+' : ''}{a.ecart_pct}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Analysis Results */}
      {aiAnalysis && (
        <div className="space-y-4" data-testid="ai-factures-results">
          <div className="kpi-card text-center border-lemas-gold/30">
            <p className="kpi-label">Score Achats</p>
            <p className={`font-mono text-4xl font-bold ${aiAnalysis.score_achats >= 70 ? 'text-lemas-green' : aiAnalysis.score_achats >= 50 ? 'text-lemas-warning' : 'text-lemas-danger'}`}>
              {aiAnalysis.score_achats}<span className="text-lg">/100</span>
            </p>
            <p className="text-sm text-[rgb(var(--lm-text-secondary))] mt-2">{aiAnalysis.resume}</p>
          </div>

          {aiAnalysis.ratio_recommandation && (
            <div className="kpi-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="kpi-label">Ratio achats/CA</p>
                  <p className={`font-mono text-2xl ${aiAnalysis.ratio_recommandation.ratio_actuel_pct <= aiAnalysis.ratio_recommandation.ratio_ideal_pct ? 'text-lemas-green' : 'text-lemas-warning'}`}>
                    {aiAnalysis.ratio_recommandation.ratio_actuel_pct}%
                  </p>
                </div>
                <div className="text-right">
                  <p className="kpi-label">Idéal</p>
                  <p className="font-mono text-2xl text-lemas-green">{aiAnalysis.ratio_recommandation.ratio_ideal_pct}%</p>
                </div>
              </div>
              <p className="text-xs text-[rgb(var(--lm-text-secondary))] mt-2">{aiAnalysis.ratio_recommandation.commentaire}</p>
            </div>
          )}

          {aiAnalysis.alertes_prix?.length > 0 && (
            <div className="kpi-card border-lemas-danger/30">
              <h4 className="text-sm font-medium text-lemas-danger mb-2 flex items-center gap-2"><AlertTriangle size={14} /> Alertes prix</h4>
              <div className="space-y-2">
                {aiAnalysis.alertes_prix.map((a, i) => (
                  <div key={i} className="p-2 rounded bg-lemas-danger/5 text-sm">
                    <div className="flex justify-between">
                      <span className="font-medium">{a.produit}</span>
                      <span className="font-mono text-lemas-danger">+{a.ecart_pct}%</span>
                    </div>
                    <p className="text-xs text-[rgb(var(--lm-text-secondary))]">Payé: {formatCurrency(a.prix_paye)} | Marché: ~{formatCurrency(a.prix_marche_estime)}</p>
                    <p className="text-xs text-[rgb(var(--lm-text-secondary))] mt-1">{a.conseil}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {aiAnalysis.fournisseurs_analyse?.length > 0 && (
            <div className="kpi-card">
              <h4 className="text-sm font-medium mb-2">Fournisseurs</h4>
              <div className="space-y-2">
                {aiAnalysis.fournisseurs_analyse.map((f, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded bg-[rgb(var(--lm-surface-elevated))] text-sm">
                    <div>
                      <span className="font-medium">{f.fournisseur}</span>
                      <span className="text-xs text-[rgb(var(--lm-text-muted))] ml-2">({f.nombre_factures} fact.)</span>
                    </div>
                    <span className="font-mono text-lemas-gold">{formatCurrency(f.total)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {aiAnalysis.optimisations?.length > 0 && (
            <div className="kpi-card border-lemas-green/30">
              <h4 className="text-sm font-medium text-lemas-green mb-2 flex items-center gap-2"><Star size={14} /> Optimisations</h4>
              <div className="space-y-2">
                {aiAnalysis.optimisations.map((o, i) => (
                  <div key={i} className={`p-2 rounded text-sm ${o.priorite === 'haute' ? 'bg-lemas-green/10' : 'bg-[rgb(var(--lm-surface-elevated))]'}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{o.titre}</span>
                      <span className="text-xs text-lemas-green font-mono">{o.economie_estimee}</span>
                    </div>
                    <p className="text-xs text-[rgb(var(--lm-text-secondary))] mt-1">{o.detail}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Food invoices table */}
      <div className="kpi-card">
        <h3 className="font-title text-lg mb-3">Factures fournisseurs ({invoices.length})</h3>
        <DataTable columns={columns} data={invoices} emptyMessage="Aucune facture ce mois" />
        {totalTTC > 0 && (
          <div className="mt-3 pt-3 border-t border-[rgb(var(--lm-border))]">
            <div className="flex justify-between items-center">
              <span className="font-medium">Total HT</span>
              <span className="font-mono text-lg text-[rgb(var(--lm-text))]">{formatCurrency(totalHT)}</span>
            </div>
            <div className="flex justify-between items-center mt-1">
              <span className="font-medium">Total TTC</span>
              <span className="font-mono text-xl text-lemas-gold">{formatCurrency(totalTTC)}</span>
            </div>
          </div>
        )}
      </div>

      {/* ============ AVOIRS & CONSIGNES & RETOURS ============ */}
      <RetoursMarchandiseSection source="patron" />
      <AvoirsSection />
      <ConsignesSection />

      {/* ============ FOURNISSEURS SANS TVA ============ */}
      <FournisseursSansTVA />

      {/* ============ DIVERSE INVOICES SECTION ============ */}
      <div className="kpi-card border-amber-500/20" data-testid="diverse-invoices-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Fuel size={18} className="text-amber-400" />
            <h3 className="font-title text-lg">Autres factures & TVA</h3>
          </div>
          {diverseInvoices.length > 0 && (
            <div className="flex items-center gap-3 text-xs">
              <span className="font-mono text-[rgb(var(--lm-text-secondary))]">{diverseInvoices.length} fact.</span>
              <span className="font-mono text-amber-400">{formatCurrency(diverseTotalTTC)} TTC</span>
              <span className="bg-red-500/15 text-red-400 px-2 py-0.5 rounded-full font-mono">TVA: {formatCurrency(diverseTotalTVA)}</span>
            </div>
          )}
        </div>

        <p className="text-xs text-[rgb(var(--lm-text-secondary))] mb-3">Essence, entretien, fournitures, assurance... Scannez pour extraire la TVA exacte.</p>

        <QuickUploadButton 
          onUpload={handleDiverseUpload} 
          endpoint="/analyze-diverse-invoice" 
          source="patron" 
          variant="gold"
          label="Scanner une facture diverse"
        />

        {/* Diverse invoices list */}
        {diverseInvoices.length > 0 && (
          <div className="mt-4 space-y-2">
            {diverseInvoices.map(inv => (
              <div key={inv.id} className="flex items-center justify-between p-3 rounded-lg bg-lemas-bg/40 border border-[rgb(var(--lm-border)/0.5)]" data-testid={`diverse-${inv.id}`}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${TYPE_COLORS[inv.type_facture] || TYPE_COLORS.divers}`}>
                    {TYPE_LABELS[inv.type_facture] || inv.type_facture}
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{inv.fournisseur}</span>
                      <span className="font-mono text-xs text-[rgb(var(--lm-text-muted))]">{inv.date}</span>
                    </div>
                    {inv.description && <p className="text-[10px] text-[rgb(var(--lm-text-secondary))] truncate">{inv.description}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0 ml-2">
                  <div className="text-right">
                    <p className="font-mono text-sm text-amber-400">{formatCurrency(inv.total_ttc)}</p>
                    <p className="font-mono text-[10px] text-red-400">TVA: {formatCurrency(inv.total_tva)}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedDiverse(inv)} className="text-[rgb(var(--lm-text-secondary))] hover:text-[rgb(var(--lm-text))]">
                    <Eye size={14} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteDiverse(inv.id)} className="text-lemas-danger hover:text-red-400">
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Food invoice detail modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setSelectedInvoice(null)}>
          <div className="bg-[rgb(var(--lm-surface))] rounded-xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-title text-xl">{selectedInvoice.fournisseur}</h3>
                <p className="text-sm text-[rgb(var(--lm-text-secondary))]">{selectedInvoice.date}</p>
              </div>
              <span className="font-mono text-2xl text-lemas-gold">{formatCurrency(selectedInvoice.total_ttc)}</span>
            </div>
            {selectedInvoice.produits?.length > 0 ? (
              <div className="mt-4">
                <h4 className="text-sm uppercase tracking-wider text-[rgb(var(--lm-text-secondary))] mb-2">Produits ({selectedInvoice.produits.length})</h4>
                <div className="space-y-1 max-h-60 overflow-y-auto">
                  {selectedInvoice.produits.map((p, i) => (
                    <div key={i} className="flex justify-between text-sm bg-lemas-bg/50 rounded p-2">
                      <div className="flex-1">
                        <span className="font-medium">{p.nom}</span>
                        <span className="text-xs text-[rgb(var(--lm-text-muted))] ml-2">[{p.categorie}]</span>
                      </div>
                      <span className="font-mono text-xs">{p.quantite} {p.unite} x {formatCurrency(p.prix_unitaire)} = <span className="text-lemas-gold">{formatCurrency(p.montant_ht)}</span></span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mt-4 p-4 rounded-lg border border-amber-500/30 bg-amber-500/5 text-center">
                <Camera className="mx-auto mb-2 text-amber-400" size={24} />
                <p className="text-sm text-amber-400 font-medium">Aucun produit extrait</p>
                <p className="text-xs text-[rgb(var(--lm-text-muted))] mt-1">Re-scannez cette facture pour extraire les articles</p>
              </div>
            )}
            {selectedInvoice.alertes?.length > 0 && (
              <div className="mt-4 space-y-1">
                {selectedInvoice.alertes.map((a, i) => (
                  <div key={i} className="text-sm text-lemas-warning bg-lemas-warning/10 rounded p-2">{a}</div>
                ))}
              </div>
            )}
            <div className="flex gap-2 mt-4">
              <button onClick={() => triggerRescan(selectedInvoice.id)} className="flex-1 flex items-center justify-center gap-2 py-2 rounded-md bg-lemas-gold/20 text-lemas-gold hover:bg-lemas-gold/30 border border-lemas-gold/30 cursor-pointer" data-testid="rescan-modal-btn">
                {rescanLoading === selectedInvoice.id ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
                Re-scanner
              </button>
              <Button onClick={() => setSelectedInvoice(null)} className="flex-1 bg-lemas-gold text-lemas-bg">Fermer</Button>
            </div>
          </div>
        </div>
      )}

      {/* Diverse invoice detail modal */}
      {selectedDiverse && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setSelectedDiverse(null)}>
          <div className="bg-[rgb(var(--lm-surface))] rounded-xl p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-title text-xl">{selectedDiverse.fournisseur}</h3>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${TYPE_COLORS[selectedDiverse.type_facture] || TYPE_COLORS.divers}`}>
                    {TYPE_LABELS[selectedDiverse.type_facture] || selectedDiverse.type_facture}
                  </span>
                </div>
                <p className="text-sm text-[rgb(var(--lm-text-secondary))]">{selectedDiverse.date}</p>
                {selectedDiverse.description && <p className="text-xs text-[rgb(var(--lm-text-secondary))] mt-1">{selectedDiverse.description}</p>}
              </div>
              <button onClick={() => setSelectedDiverse(null)} className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]"><X size={18} /></button>
            </div>

            {/* Totals */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="text-center bg-lemas-bg/50 rounded-lg p-3">
                <p className="text-xs text-[rgb(var(--lm-text-secondary))]">HT</p>
                <p className="font-mono text-lg">{formatCurrency(selectedDiverse.total_ht)}</p>
              </div>
              <div className="text-center bg-red-500/5 border border-red-500/20 rounded-lg p-3">
                <p className="text-xs text-red-400">TVA</p>
                <p className="font-mono text-lg text-red-400">{formatCurrency(selectedDiverse.total_tva)}</p>
              </div>
              <div className="text-center bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
                <p className="text-xs text-amber-400">TTC</p>
                <p className="font-mono text-lg text-amber-400">{formatCurrency(selectedDiverse.total_ttc)}</p>
              </div>
            </div>

            {/* TVA Details - THE KEY INFO */}
            {selectedDiverse.tva_details?.length > 0 && (
              <div className="mb-4" data-testid="tva-details">
                <h4 className="text-sm uppercase tracking-wider text-red-400 mb-2 flex items-center gap-1">
                  <Receipt size={14} /> Détail TVA
                </h4>
                <div className="space-y-2">
                  {selectedDiverse.tva_details.map((t, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-red-500/5 border border-red-500/10">
                      <div className="flex items-center gap-3">
                        <span className="bg-red-500/20 text-red-400 font-mono text-sm font-bold px-2 py-1 rounded">{t.taux}%</span>
                        <span className="text-sm text-[rgb(var(--lm-text-secondary))]">Base HT: <span className="font-mono text-[rgb(var(--lm-text))]">{formatCurrency(t.base_ht)}</span></span>
                      </div>
                      <span className="font-mono text-red-400 font-medium">{formatCurrency(t.montant_tva)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Lines */}
            {selectedDiverse.lignes?.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm uppercase tracking-wider text-[rgb(var(--lm-text-secondary))] mb-2">Lignes</h4>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {selectedDiverse.lignes.map((l, i) => (
                    <div key={i} className="flex justify-between text-sm bg-lemas-bg/50 rounded p-2">
                      <span className="truncate mr-2">{l.description}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="font-mono text-[rgb(var(--lm-text-secondary))]">{formatCurrency(l.montant_ht)} HT</span>
                        <span className="font-mono text-xs text-red-400">{l.taux_tva}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Deductible info */}
            <div className={`p-3 rounded-lg text-sm ${selectedDiverse.deductible ? 'bg-lemas-green/5 border border-lemas-green/20' : 'bg-orange-400/5 border border-orange-400/20'}`}>
              <span className={selectedDiverse.deductible ? 'text-lemas-green' : 'text-orange-400'}>
                {selectedDiverse.deductible ? 'TVA déductible' : 'TVA non déductible (ou partiellement)'}
              </span>
              {selectedDiverse.note && <p className="text-xs text-[rgb(var(--lm-text-secondary))] mt-1">{selectedDiverse.note}</p>}
            </div>

            {selectedDiverse.numero_facture && (
              <p className="text-xs text-[rgb(var(--lm-text-muted))] mt-3">N° facture: {selectedDiverse.numero_facture}</p>
            )}

            <Button onClick={() => setSelectedDiverse(null)} className="w-full mt-4 bg-amber-500 text-lemas-bg hover:bg-amber-600">Fermer</Button>
          </div>
        </div>
      )}
      {/* Edit invoice modal */}
      {editInvoice && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setEditInvoice(null)}>
          <div className="bg-[rgb(var(--lm-surface))] rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="edit-invoice-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-title text-lg flex items-center gap-2"><Pencil size={16} className="text-lemas-gold" /> Corriger la facture</h3>
              <button onClick={() => setEditInvoice(null)} className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Fournisseur</label>
                <input type="text" value={editForm.fournisseur} onChange={e => setEditForm({...editForm, fournisseur: e.target.value})}
                  className="w-full bg-lemas-bg border border-[rgb(var(--lm-border))] rounded-lg px-3 py-2 text-sm" data-testid="edit-fournisseur" />
              </div>
              <div>
                <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Date</label>
                <input type="date" value={editForm.date} onChange={e => setEditForm({...editForm, date: e.target.value})}
                  className="w-full bg-lemas-bg border border-[rgb(var(--lm-border))] rounded-lg px-3 py-2 text-sm" data-testid="edit-date" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Total HT</label>
                  <input type="number" step="0.01" value={editForm.total_ht} onChange={e => setEditForm({...editForm, total_ht: parseFloat(e.target.value) || 0})}
                    className="w-full bg-lemas-bg border border-[rgb(var(--lm-border))] rounded-lg px-3 py-2 text-sm font-mono" data-testid="edit-total-ht" />
                </div>
                <div>
                  <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">TVA</label>
                  <input type="number" step="0.01" value={editForm.total_tva} onChange={e => setEditForm({...editForm, total_tva: parseFloat(e.target.value) || 0})}
                    className="w-full bg-lemas-bg border border-[rgb(var(--lm-border))] rounded-lg px-3 py-2 text-sm font-mono" data-testid="edit-total-tva" />
                </div>
                <div>
                  <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Total TTC</label>
                  <input type="number" step="0.01" value={editForm.total_ttc} onChange={e => setEditForm({...editForm, total_ttc: parseFloat(e.target.value) || 0})}
                    className="w-full bg-lemas-bg border border-[rgb(var(--lm-border))] rounded-lg px-3 py-2 text-sm font-mono text-lemas-gold" data-testid="edit-total-ttc" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Cuisine HT</label>
                  <input type="number" step="0.01" value={editForm.cuisine_ht} onChange={e => setEditForm({...editForm, cuisine_ht: parseFloat(e.target.value) || 0})}
                    className="w-full bg-lemas-bg border border-[rgb(var(--lm-border))] rounded-lg px-3 py-2 text-sm font-mono" />
                </div>
                <div>
                  <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Boissons HT</label>
                  <input type="number" step="0.01" value={editForm.boissons_ht} onChange={e => setEditForm({...editForm, boissons_ht: parseFloat(e.target.value) || 0})}
                    className="w-full bg-lemas-bg border border-[rgb(var(--lm-border))] rounded-lg px-3 py-2 text-sm font-mono" />
                </div>
                <div>
                  <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Non-alim HT</label>
                  <input type="number" step="0.01" value={editForm.non_alimentaire_ht} onChange={e => setEditForm({...editForm, non_alimentaire_ht: parseFloat(e.target.value) || 0})}
                    className="w-full bg-lemas-bg border border-[rgb(var(--lm-border))] rounded-lg px-3 py-2 text-sm font-mono" />
                </div>
              </div>
            </div>
            <Button onClick={saveEdit} className="w-full mt-4 bg-lemas-gold text-lemas-bg hover:bg-lemas-gold/90" data-testid="save-edit-btn">
              <Save size={14} className="mr-1" /> Enregistrer
            </Button>
          </div>
        </div>
      )}

      {/* Manual invoice entry modal */}
      {showManualForm && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setShowManualForm(false)}>
          <div className="bg-[rgb(var(--lm-surface))] rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="manual-invoice-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-title text-lg flex items-center gap-2"><Plus size={16} className="text-lemas-gold" /> Saisie manuelle</h3>
              <button onClick={() => setShowManualForm(false)} className="text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Fournisseur *</label>
                <input type="text" value={manualForm.fournisseur} onChange={e => setManualForm({...manualForm, fournisseur: e.target.value})}
                  placeholder="Ex: Metro, Promocash, Brake..."
                  className="w-full bg-lemas-bg border border-[rgb(var(--lm-border))] rounded-lg px-3 py-2 text-sm" data-testid="manual-fournisseur" />
              </div>
              <div>
                <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Date</label>
                <input type="date" value={manualForm.date} onChange={e => setManualForm({...manualForm, date: e.target.value})}
                  className="w-full bg-lemas-bg border border-[rgb(var(--lm-border))] rounded-lg px-3 py-2 text-sm" data-testid="manual-date" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Total HT</label>
                  <input type="number" step="0.01" value={manualForm.total_ht} onChange={e => setManualForm({...manualForm, total_ht: e.target.value})}
                    placeholder="0.00"
                    className="w-full bg-lemas-bg border border-[rgb(var(--lm-border))] rounded-lg px-3 py-2 text-sm font-mono" data-testid="manual-total-ht" />
                </div>
                <div>
                  <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">TVA</label>
                  <input type="number" step="0.01" value={manualForm.total_tva} onChange={e => setManualForm({...manualForm, total_tva: e.target.value})}
                    placeholder="0.00"
                    className="w-full bg-lemas-bg border border-[rgb(var(--lm-border))] rounded-lg px-3 py-2 text-sm font-mono" data-testid="manual-total-tva" />
                </div>
                <div>
                  <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Total TTC *</label>
                  <input type="number" step="0.01" value={manualForm.total_ttc} onChange={e => setManualForm({...manualForm, total_ttc: e.target.value})}
                    placeholder="0.00"
                    className="w-full bg-lemas-bg border border-[rgb(var(--lm-border))] rounded-lg px-3 py-2 text-sm font-mono text-lemas-gold" data-testid="manual-total-ttc" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Cuisine HT</label>
                  <input type="number" step="0.01" value={manualForm.cuisine_ht} onChange={e => setManualForm({...manualForm, cuisine_ht: e.target.value})}
                    placeholder="0.00"
                    className="w-full bg-lemas-bg border border-[rgb(var(--lm-border))] rounded-lg px-3 py-2 text-sm font-mono" data-testid="manual-cuisine-ht" />
                </div>
                <div>
                  <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Boissons HT</label>
                  <input type="number" step="0.01" value={manualForm.boissons_ht} onChange={e => setManualForm({...manualForm, boissons_ht: e.target.value})}
                    placeholder="0.00"
                    className="w-full bg-lemas-bg border border-[rgb(var(--lm-border))] rounded-lg px-3 py-2 text-sm font-mono" data-testid="manual-boissons-ht" />
                </div>
                <div>
                  <label className="text-xs text-[rgb(var(--lm-text-secondary))] block mb-1">Non-alim HT</label>
                  <input type="number" step="0.01" value={manualForm.non_alimentaire_ht} onChange={e => setManualForm({...manualForm, non_alimentaire_ht: e.target.value})}
                    placeholder="0.00"
                    className="w-full bg-lemas-bg border border-[rgb(var(--lm-border))] rounded-lg px-3 py-2 text-sm font-mono" data-testid="manual-non-alim-ht" />
                </div>
              </div>
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">* Champs obligatoires. Remplissez au minimum le fournisseur et un montant.</p>
            </div>
            <Button onClick={handleManualSubmit} disabled={manualSaving} className="w-full mt-4 bg-lemas-gold text-lemas-bg hover:bg-lemas-gold/90" data-testid="save-manual-btn">
              {manualSaving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
              Enregistrer la facture
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const FournisseursSansTVA = () => {
  const [configs, setConfigs] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newNom, setNewNom] = useState('');
  const [newPays, setNewPays] = useState('France');
  const [newSansTva, setNewSansTva] = useState(false);

  const fetchConfigs = async () => {
    try {
      const res = await api.get('/fournisseurs-config');
      setConfigs(res.data || []);
    } catch {}
  };

  useEffect(() => { fetchConfigs(); }, []);

  const addFournisseur = async () => {
    if (!newNom.trim()) return;
    try {
      const res = await api.post('/fournisseurs-config', {
        fournisseur: newNom.trim(),
        sans_tva: newSansTva,
        pays: newPays,
      });
      if (res.data.error) { toast.error(res.data.error); return; }
      toast.success(res.data.message);
      setNewNom('');
      setNewSansTva(false);
      setNewPays('France');
      setShowAdd(false);
      fetchConfigs();
    } catch { toast.error('Erreur'); }
  };

  const toggleTva = async (config) => {
    try {
      const res = await api.post('/fournisseurs-config', {
        fournisseur: config.nom,
        sans_tva: !config.sans_tva,
        pays: config.pays || '',
      });
      if (res.data.error) { toast.error(res.data.error); return; }
      toast.success(res.data.message);
      fetchConfigs();
    } catch { toast.error('Erreur'); }
  };

  const removeFournisseur = async (nom) => {
    try {
      await api.delete(`/fournisseurs-config/${encodeURIComponent(nom)}`);
      toast.success('Supprimé');
      fetchConfigs();
    } catch { toast.error('Erreur'); }
  };

  return (
    <div className="kpi-card border-blue-400/20" data-testid="fournisseurs-config-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Receipt size={16} className="text-blue-400" />
          <h3 className="text-sm font-medium">Fournisseurs & TVA</h3>
        </div>
        <Button size="sm" onClick={() => setShowAdd(!showAdd)}
          className={showAdd ? 'bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text))]' : 'bg-blue-400/20 text-blue-400 hover:bg-blue-400/30'}
          data-testid="add-fournisseur-btn">
          {showAdd ? <X size={14} /> : <><Plus size={14} className="mr-1" /> Ajouter</>}
        </Button>
      </div>

      {/* Add form */}
      {showAdd && (
        <div className="bg-lemas-bg/60 rounded-lg p-3 mb-3 space-y-2 animate-slide-up" data-testid="fournisseur-add-form">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <input
              value={newNom}
              onChange={e => setNewNom(e.target.value)}
              placeholder="Nom du fournisseur"
              className="col-span-2 sm:col-span-1 bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] rounded-md px-3 py-1.5 text-sm text-[rgb(var(--lm-text))]"
              data-testid="fournisseur-nom-input"
              onKeyDown={e => e.key === 'Enter' && addFournisseur()}
            />
            <select value={newPays} onChange={e => setNewPays(e.target.value)}
              className="bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] rounded-md px-2 text-sm text-[rgb(var(--lm-text))]"
              data-testid="fournisseur-pays-select">
              <option value="France">France</option>
              <option value="Espagne">Espagne</option>
              <option value="Portugal">Portugal</option>
              <option value="Italie">Italie</option>
              <option value="Allemagne">Allemagne</option>
              <option value="Autre UE">Autre UE</option>
              <option value="Hors UE">Hors UE</option>
            </select>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 cursor-pointer" data-testid="fournisseur-tva-toggle">
                <div className={`relative w-9 h-5 rounded-full transition-colors ${newSansTva ? 'bg-red-400' : 'bg-green-400'}`}
                  onClick={() => setNewSansTva(!newSansTva)}>
                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${newSansTva ? 'translate-x-4.5 left-[1px]' : 'left-[2px]'}`}
                    style={{ transform: newSansTva ? 'translateX(17px)' : 'translateX(0)' }} />
                </div>
                <span className="text-xs text-[rgb(var(--lm-text-secondary))]">
                  {newSansTva ? 'Sans TVA' : 'Avec TVA'}
                </span>
              </label>
            </div>
          </div>
          <div className="flex justify-end">
            <Button size="sm" onClick={addFournisseur} className="bg-blue-500 text-white text-xs" data-testid="save-fournisseur-btn">
              <Save size={14} className="mr-1" /> Enregistrer
            </Button>
          </div>
        </div>
      )}

      {/* Suppliers list */}
      {configs.length > 0 ? (
        <div className="space-y-1.5">
          {configs.map(c => (
            <div key={c.nom_upper} className="flex items-center justify-between py-2 px-3 rounded-lg bg-lemas-bg/40 border border-[rgb(var(--lm-border)/0.5)]" data-testid={`fournisseur-row-${c.nom_upper}`}>
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-sm font-medium text-[rgb(var(--lm-text))] truncate">{c.nom}</span>
                {c.pays && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))] shrink-0">{c.pays}</span>}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <button onClick={() => toggleTva(c)} className="flex items-center gap-1.5 cursor-pointer" data-testid={`toggle-tva-${c.nom_upper}`}>
                  <div className={`relative w-9 h-5 rounded-full transition-colors ${c.sans_tva ? 'bg-red-400' : 'bg-green-400'}`}>
                    <div className="absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform"
                      style={{ transform: c.sans_tva ? 'translateX(17px)' : 'translateX(2px)' }} />
                  </div>
                  <span className={`text-[10px] font-medium ${c.sans_tva ? 'text-red-400' : 'text-green-400'}`}>
                    {c.sans_tva ? 'Sans TVA' : 'TVA'}
                  </span>
                </button>
                <button onClick={() => removeFournisseur(c.nom)} className="text-[rgb(var(--lm-text-muted))] hover:text-red-400 transition-colors" data-testid={`delete-fournisseur-${c.nom_upper}`}>
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : !showAdd && (
        <p className="text-xs text-[rgb(var(--lm-text-muted))] text-center py-1">Aucun fournisseur configuré</p>
      )}
    </div>
  );
};

export default PatronFactures;
